import { useId, cloneElement, isValidElement } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { HelpTooltip } from '@commerce-os/help-client';

interface FieldProps {
  /**
   * Visible label text. Optional so consumers can render a label-less
   * row inside a vertical Field stack (e.g. inventory PO receive grid:
   * label only on the first row, blank on subsequent rows).
   */
  label?: string;
  /** Show a red asterisk next to the label. */
  isRequired?: boolean;
  /** Add a grey "(optional)" suffix after the label. */
  isOptional?: boolean;
  /** Error message shown beneath the input. */
  error?: string;
  /** Secondary helper text shown beneath the input (hidden when `error` is set). */
  helperText?: string;
  children: ReactNode;
  className?: string;
  /**
   * When set, renders a `<HelpTooltip helpKey={...}>` after the label.
   * The icon is hidden when the caller isn't entitled OR the helpKey isn't
   * registered (gating-via-invisibility per the Phase 35 contract).
   * Phase 35.3c launch contract — every Field consumer can opt in by
   * passing a helpKey from `docs/help/tooltips/INVENTORY.md` without
   * re-wiring the form.
   */
  helpKey?: string;
}

/**
 * Renders a visible label block around form controls.
 *
 * HeroUI's Input / Select / NumberField / TextArea accept a `label`
 * prop but only forward it as `aria-label` — nothing renders on screen.
 * Wrap those controls in <Field> so users can actually see what each
 * input is for.
 *
 * The label is a semantic <label> element tied to the child control
 * via an auto-generated `id` + `htmlFor`. If the caller already passed
 * an `id` on the child we respect it instead.
 *
 * The error / helper paragraph is wired the same way: it carries an id
 * derived from the generated one, the child is described by it
 * (`aria-describedby`, joined with any value the caller already set), and
 * an error additionally marks the child `aria-invalid` and announces
 * itself (`role="alert"`). Without this a screen-reader user who submits
 * an invalid value hears the field name and nothing else — the red text
 * below is invisible to them.
 */
export default function Field({
  label,
  isRequired,
  isOptional,
  error,
  helperText,
  children,
  className,
  helpKey,
}: FieldProps) {
  const generatedId = useId();
  const message = error || helperText;
  const messageId = message ? `${generatedId}-message` : undefined;

  // If the wrapped control already has an id (e.g. react-hook-form
  // consumers), use it so we don't clobber their wiring.
  const childWithId = (() => {
    if (!isValidElement(children)) {
      return { element: children, id: generatedId };
    }
    const element = children as ReactElement<{
      id?: string;
      'aria-describedby'?: string;
      'aria-invalid'?: boolean;
    }>;
    const id = element.props.id || generatedId;
    const describedBy =
      [element.props['aria-describedby'], messageId].filter(Boolean).join(' ') ||
      undefined;
    return {
      element: cloneElement(element, {
        id,
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        ...(error ? { 'aria-invalid': true } : {}),
      }),
      id,
    };
  })();

  return (
    <div className={className}>
      {label !== undefined && (
        <label
          htmlFor={childWithId.id}
          className="text-sm text-default-500 block mb-1"
        >
          {label}
          {isRequired && <span className="text-danger"> *</span>}
          {isOptional && <span className="text-default-400"> (optional)</span>}
          {helpKey && (
            <span className="ml-1.5 align-middle inline-flex">
              <HelpTooltip helpKey={helpKey} />
            </span>
          )}
        </label>
      )}
      {childWithId.element}
      {error ? (
        <p id={messageId} role="alert" className="text-danger text-xs mt-1">
          {error}
        </p>
      ) : helperText ? (
        <p id={messageId} className="text-default-400 text-xs mt-1">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
